import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Truck, Clock, CheckCircle, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@flote/ui';
import { toast } from 'sonner';
import { TaskFormModal, type TaskFormData } from '../../components/tasks';
import { TaskStatusBadge, TaskTypeBadge } from '../../components/status-badges';
import { deliveryTsr, deliveryClient } from '../../api';
import type { DeliveryTaskStatus, DeliveryTaskType } from '@flote/types/delivery';
import type { Driver } from '@flote/contracts/http';

function TaskStatusCard({
  title,
  count,
  icon: Icon,
  color,
  isLoading,
}: {
  title: string;
  count: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-4">
        <div className={`rounded-full p-2 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          {isLoading ? (
            <div className="h-8 w-8 animate-pulse rounded bg-muted" />
          ) : (
            <p className="text-2xl font-bold">{count}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function TasksPage() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Fetch dashboard stats for status counts
  const { data: statsResponse, isLoading: statsLoading } = deliveryTsr.adminDashboard.getStats.useQuery({
    queryKey: ['dashboard-stats'],
  });
  const stats = statsResponse?.status === 200 ? statsResponse.body : null;

  // Fetch task list
  const { data: tasksResponse, isLoading: tasksLoading, refetch } = deliveryTsr.adminTasks.list.useQuery({
    queryKey: ['tasks-list'],
    queryData: { query: { limit: 50 } },
  });
  const tasks = tasksResponse?.status === 200 ? tasksResponse.body.items : [];

  // Fetch teams for task assignment
  const { data: teamsResponse } = deliveryTsr.adminTeams.list.useQuery({
    queryKey: ['teams-list'],
  });
  const teams = teamsResponse?.status === 200 ? teamsResponse.body : [];

  // Fetch recipients for task selection
  const { data: recipientsResponse } = deliveryTsr.adminRecipients.list.useQuery({
    queryKey: ['recipients-list'],
  });
  const recipients = recipientsResponse?.status === 200 ? recipientsResponse.body : [];

  // Fetch destinations for task selection
  const { data: destinationsResponse } = deliveryTsr.adminDestinations.list.useQuery({
    queryKey: ['destinations-list'],
  });
  const destinations = destinationsResponse?.status === 200 ? destinationsResponse.body : [];

  // Fetch B2BWave orders for task creation (deliverable statuses defined in DELIVERABLE_ORDER_STATUSES)
  const { data: ordersResponse } = deliveryTsr.adminOrders.list.useQuery({
    queryKey: ['orders-list'],
    queryData: { query: {} },
  });
  const orders = ordersResponse?.status === 200 ? ordersResponse.body : [];

  // Mutations
  const createTaskMutation = deliveryTsr.adminTasks.create.useMutation();
  const createLinkedMutation = deliveryTsr.adminTasks.createLinked.useMutation();
  const syncTasksMutation = deliveryTsr.adminTasks.sync.useMutation();

  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    try {
      const response = await syncTasksMutation.mutateAsync({
        body: {},
      });

      if (response.status === 200) {
        toast.success(`Synced ${response.body.synced} tasks from Onfleet`);
        refetch();
      } else {
        const errorBody = response.body as { message?: string };
        toast.error(errorBody?.message || 'Failed to sync tasks');
      }
    } catch (error) {
      toast.error('Failed to sync tasks');
      console.error('Sync tasks error:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [syncTasksMutation, refetch]);

  const handleCreateTask = useCallback(async (formData: TaskFormData) => {
    try {
      if (formData.type === 'linked') {
        // Create linked pickup+dropoff tasks
        const result = await createLinkedMutation.mutateAsync({ body: formData.data });
        if (result.status === 201) {
          toast.success('Pickup and dropoff tasks created successfully!');
          setIsModalOpen(false);
          refetch();
        } else {
          const errorBody = result.body as { message?: string };
          toast.error(errorBody?.message || 'Failed to create linked tasks');
        }
      } else {
        // Create single task (pickup or dropoff)
        const result = await createTaskMutation.mutateAsync({ body: formData.data });
        if (result.status === 201) {
          toast.success('Task created successfully!');
          setIsModalOpen(false);
          refetch();
        } else {
          const errorBody = result.body as { message?: string };
          toast.error(errorBody?.message || 'Failed to create task');
        }
      }
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    }
  }, [createTaskMutation, createLinkedMutation, refetch]);

  const fetchDriversByTeam = useCallback(async (teamId: string): Promise<Driver[]> => {
    const response = await deliveryClient.adminDrivers.list({ query: { teamId } });
    if (response.status === 200) {
      return response.body;
    }
    return [];
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tasks</h1>
          <p className="text-muted-foreground">
            Manage and track delivery tasks
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={isSyncing}
            className="w-full sm:w-auto"
          >
            {isSyncing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Sync from Onfleet
          </Button>
          <Button onClick={() => setIsModalOpen(true)} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            New Task
          </Button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <TaskStatusCard
          title="Pending"
          count={stats?.pendingTasks ?? 0}
          icon={AlertCircle}
          color="bg-yellow-100 text-yellow-600"
          isLoading={statsLoading}
        />
        <TaskStatusCard
          title="In Transit"
          count={stats?.inTransitTasks ?? 0}
          icon={Truck}
          color="bg-blue-100 text-blue-600"
          isLoading={statsLoading}
        />
        <TaskStatusCard
          title="Completed Today"
          count={stats?.completedToday ?? 0}
          icon={CheckCircle}
          color="bg-green-100 text-green-600"
          isLoading={statsLoading}
        />
        <TaskStatusCard
          title="Failed Today"
          count={stats?.failedToday ?? 0}
          icon={Clock}
          color="bg-red-100 text-red-600"
          isLoading={statsLoading}
        />
      </div>

      {/* Task List */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          {tasksLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Truck className="mb-4 h-12 w-12 text-muted-foreground/50" />
              <h3 className="mb-2 text-lg font-medium">No tasks yet</h3>
              <p className="mb-4 max-w-sm text-sm text-muted-foreground">
                Create your first delivery task to get started with Onfleet integration.
              </p>
              <Button onClick={() => setIsModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Create Task
              </Button>
            </div>
          ) : (
            <div className="-mx-4 overflow-x-auto sm:mx-0">
              <div className="inline-block min-w-full align-middle">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tracking ID</TableHead>
                      <TableHead className="hidden sm:table-cell">Type</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead className="hidden sm:table-cell">Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden md:table-cell">Driver</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tasks.map((task) => (
                      <TableRow
                        key={task.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/tasks/${task.id}`)}
                      >
                        <TableCell className="font-mono text-sm">{task.trackingId}</TableCell>
                        <TableCell className="hidden sm:table-cell">
                          <TaskTypeBadge
                            taskType={(task.taskType as DeliveryTaskType) || 'dropoff'}
                            linkedTaskId={task.linkedTaskId}
                          />
                        </TableCell>
                        <TableCell>{task.recipientName}</TableCell>
                        <TableCell className="hidden text-muted-foreground sm:table-cell">
                          {task.address.city}, {task.address.state}
                        </TableCell>
                        <TableCell>
                          <TaskStatusBadge status={task.status as DeliveryTaskStatus} />
                        </TableCell>
                        <TableCell className="hidden md:table-cell">{task.driver?.name || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task Creation Modal */}
      <TaskFormModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSubmit={handleCreateTask}
        isProcessing={createTaskMutation.isPending}
        teams={teams}
        recipients={recipients}
        destinations={destinations}
        orders={orders}
        onFetchDriversByTeam={fetchDriversByTeam}
      />
    </div>
  );
}
